import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, AlertCircle, AlertTriangle, CheckCircle2, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import SeoHead from "../components/SeoHead";
import ReadingProgressBar from "../components/ReadingProgressBar";
import WatchButton from "../components/WatchButton";
import IngredientSubmissionCTA from "../components/IngredientSubmissionCTA";
import BarcodeSubmissionCTA from "../components/BarcodeSubmissionCTA";
import PhotoSubmissionCTA from "../components/PhotoSubmissionCTA";
import BrandProductLabel from "../components/BrandProductLabel";
import { bucketIngredients, splitBrandAndName } from "../lib/productDisplay";

// User-facing chips — HIGH / MEDIUM / LOW only. The 6-tier admin
// taxonomy never reaches consumers; we surface its human-readable label
// (e.g. "High Irritation Risk") as a small sub-line under the chip.
const SEVERITY_VISUAL = {
  HIGH: {
    chip: "bg-rose-100 text-rose-800 border-rose-200",
    Icon: AlertCircle,
    label: "HIGH",
  },
  MEDIUM: {
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
    label: "MEDIUM",
  },
  LOW: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: CheckCircle2,
    label: "LOW",
  },
};

// Human-readable label for the 6-tier admin severity → shown as sub-line.
const SEVERITY_SUBLABEL = {
  CONTRAINDICATED:      "Avoid entirely",
  HIGH_IRRITATION_RISK: "High irritation risk",
  WARNING:              "May increase sensitivity",
  CAUTION:              "Use with caution",
  DUPLICATE_ACTIVE:     "Duplicate active",
  INFO:                 "Educational note",
};

// Map raw row → which tier chip to show. Prefers ``severity_v2`` when set
// by the migration; falls back to legacy ``severity`` (HIGH/MEDIUM/LOW).
function resolveTier(ix) {
  const sv2 = ix.severity_v2;
  if (sv2 === "CONTRAINDICATED" || sv2 === "HIGH_IRRITATION_RISK") return "HIGH";
  if (sv2 === "WARNING") return "MEDIUM";
  if (sv2 === "DUPLICATE_ACTIVE") return "MEDIUM";
  if (sv2 === "CAUTION") return "LOW";
  if (sv2 === "INFO") return "LOW";
  // Legacy HIGH/MODERATE/LOW
  const legacy = (ix.severity || "").toUpperCase();
  if (legacy === "MODERATE") return "MEDIUM";
  return SEVERITY_VISUAL[legacy] ? legacy : "MEDIUM";
}

function Skeleton() {
  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="product-page-skeleton">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 space-y-6 animate-pulse">
        <div className="h-3 w-1/4 bg-cream-200 rounded" />
        <div className="h-12 w-3/4 bg-cream-200 rounded" />
        <div className="h-24 w-full bg-cream-200 rounded-2xl" />
        <div className="h-32 w-full bg-cream-200 rounded-2xl" />
        <div className="h-40 w-full bg-cream-200 rounded-2xl" />
      </div>
    </main>
  );
}

function NotFound({ slug }) {
  return (
    <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5">
      <SeoHead
        title="Product not found — Blendsafe"
        description="We couldn't find that product in our catalog. Search for an alternative."
        canonicalPath={`/products/${slug}`}
      />
      <div
        data-testid="product-page-not-found"
        className="max-w-md text-center space-y-5"
      >
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
          Product not found.
        </h1>
        <p className="text-ink-soft">
          We couldn't find that product in our catalog. Browse our database or
          run a check by typing the ingredient list.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/browse"
            data-testid="product-page-browse-cta"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Browse products <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/check"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl border border-ink text-ink font-bold hover:bg-ink hover:text-cream-50 transition-all"
          >
            Run a manual check
          </Link>
        </div>
      </div>
    </main>
  );
}

function ProductView({ data, onAuthNeeded }) {
  const product = data.product || {};
  const interactions = data.known_interactions || [];
  const { brand: bShow, name: nShow } = splitBrandAndName(product);

  const buckets = useMemo(
    () => bucketIngredients(data.key_actives || []),
    [data.key_actives],
  );
  const actives = data.key_actives || [];
  const [showFull, setShowFull] = useState(false);

  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="product-page">
      <ReadingProgressBar />
      <SeoHead
        title={data.title}
        description={data.meta_description}
        canonicalPath={`/products/${product.slug}`}
      />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-8">
        {bShow && (
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-ink-mute">
            {bShow}
          </p>
        )}
        {product.image_url && (
          <figure
            data-testid="product-page-hero-image"
            className="rounded-2xl overflow-hidden bg-white border border-surface-line"
          >
            <img
              src={
                product.image_url.startsWith("/api/")
                  ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}`
                  : product.image_url
              }
              alt={`${bShow || product.brand || ""} ${nShow || product.name || ""}`.trim()}
              loading="lazy"
              className="block w-full max-h-72 object-contain bg-cream-50"
            />
          </figure>
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1
            data-testid="product-page-h1"
            className="text-3xl sm:text-5xl font-extrabold tracking-[-0.02em] text-ink leading-[1.05] flex-1 min-w-0"
          >
            {nShow || product.name}
          </h1>
          <WatchButton
            kind="product"
            targetId={product.id}
            labelHint={nShow || product.name}
            onAuthNeeded={onAuthNeeded}
            className="mt-2"
          />
        </div>
        <p className="text-ink-soft text-[15px] leading-relaxed">
          Ingredient compatibility check from Blendsafe — every active
          cross-referenced against our medication interaction database.
        </p>

        {/* Section A — Active ingredients */}
        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-bold text-ink">Active ingredients</h2>
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute">
              {buckets.actives.length} found
            </span>
          </div>
          <p className="text-[13px] text-ink-soft leading-relaxed">
            Functional actives that drive the product's effect — exfoliants,
            retinoids, brighteners, antioxidants, and other treatment ingredients.
          </p>
          {buckets.actives.length === 0 ? (
            <p className="text-[14px] text-ink-mute">
              No major functional actives detected — this looks like a supportive
              or barrier-focused formula.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="product-page-actives">
              {buckets.actives.map((a) => (
                <Link
                  key={a.id}
                  to={`/ingredients/${a.slug}`}
                  data-testid={`product-page-active-${a.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-rose-50 border border-rose-200 text-[13px] font-semibold text-rose-900 hover:bg-rose-100 hover:border-rose-300 transition-colors"
                >
                  {a.name}
                  {a.category && (
                    <span className="text-[10px] uppercase tracking-[0.12em] font-medium text-rose-700/80">
                      · {a.category}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Section B — Supporting / Barrier ingredients */}
        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-bold text-ink">Supporting & barrier ingredients</h2>
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute">
              {buckets.supporting.length} found
            </span>
          </div>
          <p className="text-[13px] text-ink-soft leading-relaxed">
            Hydrators, ceramides, soothers, and barrier-supportive ingredients
            that keep the formula tolerable and the skin barrier healthy.
          </p>
          {buckets.supporting.length === 0 ? (
            <p className="text-[14px] text-ink-mute">
              No supporting / barrier ingredients identified in our database.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="product-page-supporting">
              {buckets.supporting.map((a) => (
                <Link
                  key={a.id}
                  to={`/ingredients/${a.slug}`}
                  data-testid={`product-page-supporting-${a.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-emerald-50 border border-emerald-200 text-[13px] font-semibold text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                >
                  {a.name}
                  {a.category && (
                    <span className="text-[10px] uppercase tracking-[0.12em] font-medium text-emerald-700/80">
                      · {a.category}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Flagged medication interactions */}
        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
          <h2 className="text-lg font-bold text-ink">Flagged medication interactions</h2>
          {interactions.length === 0 ? (
            <p className="text-ink-soft text-[14px]">
              No flagged interactions against the prescription medications in
              our database.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="product-page-interactions">
              {interactions.map((ix, i) => {
                const tier = resolveTier(ix);
                const v = SEVERITY_VISUAL[tier];
                const Icon = v.Icon;
                const slug = ix.medication_slug;
                const subLabel = ix.severity_v2 ? SEVERITY_SUBLABEL[ix.severity_v2] : null;
                return (
                  <li
                    key={`${ix.medication}-${i}`}
                    className="flex items-start gap-3 pb-3 last:pb-0 border-b last:border-b-0 border-surface-line"
                  >
                    <span className="shrink-0 mt-0.5 flex flex-col items-start gap-0.5">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full border font-bold text-[11px] uppercase tracking-wide ${v.chip}`}
                      >
                        <Icon className="w-3 h-3" /> {v.label}
                      </span>
                      {subLabel && (
                        <span
                          data-testid={`severity-sublabel-${i}`}
                          className="text-[10.5px] text-ink-mute font-medium leading-tight pl-1"
                        >
                          {subLabel}
                        </span>
                      )}
                    </span>
                    <div className="text-[14px] leading-relaxed">
                      {slug ? (
                        <Link
                          to={`/medications/${slug}`}
                          className="font-bold text-ink hover:underline underline-offset-4"
                        >
                          {ix.medication}
                        </Link>
                      ) : (
                        <span className="font-bold text-ink">{ix.medication}</span>
                      )}
                      {ix.summary && (
                        <span className="text-ink-soft"> — {ix.summary}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* CTA: ask the community to fix incomplete data */}
        {(product.data_confidence === "LOW" || product.data_confidence === "MEDIUM") && (
          <IngredientSubmissionCTA
            productId={product.id}
            tier={product.data_confidence}
            hits={product.canonical_hits}
            total={product.total_tokens}
          />
        )}

        {/* CTA: collect barcodes for products that aren't yet scannable */}
        {!product.barcode_upc && (
          <BarcodeSubmissionCTA productId={product.id} />
        )}

        {/* CTA: collect community-submitted product imagery */}
        {!product.image_url && (
          <PhotoSubmissionCTA productId={product.id} />
        )}

        {/* Section C — Full INCI ingredient list (collapsible) */}
        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-3">
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            data-testid="product-page-inci-toggle"
            className="w-full flex items-center justify-between gap-3 text-left"
            aria-expanded={showFull}
          >
            <h2 className="text-lg font-bold text-ink">Full INCI ingredient list</h2>
            <ChevronDown
              className={`w-5 h-5 text-ink-mute transition-transform ${showFull ? "rotate-180" : ""}`}
            />
          </button>
          {showFull && (
            <p
              data-testid="product-page-inci"
              className="text-[13px] leading-[1.65] text-ink-soft bg-cream-100 border border-surface-line rounded-xl p-4"
            >
              {product.ingredients_text || (
                <em className="text-ink-mute">
                  Full ingredient list not yet available for this product.
                </em>
              )}
            </p>
          )}
          {!showFull && (
            <p className="text-[12px] text-ink-mute">
              Click to expand the complete INCI list as printed on the package.
            </p>
          )}
        </section>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Link
            to="/routine"
            data-testid="product-page-cta"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Add to routine check <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/check"
            className="text-sm font-semibold text-ink-soft underline decoration-from-font underline-offset-4 hover:text-ink"
          >
            or run a single check
          </Link>
        </div>

        <p className="text-[11px] text-ink-mute pt-4 border-t border-surface-line">
          Educational content — not medical advice. Ingredient lists may change
          over time; always confirm with the package label.
        </p>
      </div>
    </main>
  );
}

export default function ProductPage({ onAuthNeeded }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    api
      .get(`/seo/products/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setStatus("ok");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("notfound");
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === "loading") return <Skeleton />;
  if (status === "notfound") return <NotFound slug={slug} />;
  return <ProductView data={data} onAuthNeeded={onAuthNeeded} />;
}
